import { getAccessScopeId, requireAccessScope } from "@lib/access/access";

export function withURLParam(url: string, values: { [key: string]: any }): string {
    const params = new URLSearchParams();
    Object.keys(values)
        .map((key) => ({ key, value: values[key] })) // unpack
        .map(({ key, value }) => ({ key, value: String(value) })) // to string
        .filter(({ value }) => value && value.length)
        .forEach(({ key, value }) => params.append(key, value));
    const result = params.toString();
    return `${url}${result.length ? `?${result}` : ""}`;
}

function buildAuthHeaders(scopeId: string | null) {
    return {
        token: localStorage.getItem(`Meteor.loginToken`) || ``,
        userId: localStorage.getItem(`Meteor.userId`) || ``,
        logasId: localStorage.getItem(`side_team_logasId`) || ``,
        organisationId: scopeId || ``,
    } satisfies HeadersInit;
}

function withScopedAuthHeaders(
    input: string | Request,
    scopeId: string | null,
    init?: RequestInit,
) {
    const headers = new Headers(input instanceof Request ? input.headers : undefined);

    new Headers(buildAuthHeaders(scopeId)).forEach((value, key) => {
        headers.set(key, value);
    });

    if (init?.headers) {
        new Headers(init.headers).forEach((value, key) => {
            headers.set(key, value);
        });
    }

    return new Request(input, {
        credentials: "omit",
        ...init,
        headers,
    });
}

/** Adds auth headers for operations that require an active account. */
export function withAccountAuthHeaders(input: string | Request, init?: RequestInit) {
    const accountId = requireAccessScope("account");
    return withScopedAuthHeaders(input, accountId, init);
}

/** Adds auth headers for authenticated operations, using the active account or network when available. */
export function withAuthenticatedHeaders(input: string | Request, init?: RequestInit) {
    requireAccessScope("authenticated");
    return withScopedAuthHeaders(input, getAccessScopeId("accountOrNetwork"), init);
}

/** Adds any available headers without requiring a session, for sign-in and activation endpoints. */
export function withPublicAuthHeaders(input: string | Request, init?: RequestInit) {
    return withScopedAuthHeaders(
        input,
        localStorage.getItem(`side_team_activeOrganisationId`),
        init,
    );
}

/** Adds auth headers for operations that require an active network. */
export function withNetworkAuthHeaders(input: string | Request, init?: RequestInit) {
    const networkId = requireAccessScope("network");
    return withScopedAuthHeaders(input, networkId, init);
}

/** Returns the account ID when available, otherwise the active network ID. */
export function getAccountOrNetworkId() {
    return getAccessScopeId("accountOrNetwork");
}

/** Adds auth headers for operations supported in either an account or network context. */
export function withAccountOrNetworkAuthHeaders(input: string | Request, init?: RequestInit) {
    const accountOrNetworkId = requireAccessScope("accountOrNetwork");
    return withScopedAuthHeaders(input, accountOrNetworkId, init);
}

/** Uses account-or-network access while targeting a specific child organisation. */
export function withAccountOrNetworkAuthHeadersForOrganisation(
    input: string | Request,
    organisationId: string,
    init?: RequestInit,
) {
    const headers = new Headers(init?.headers);
    headers.set("organisationId", organisationId);
    return withAccountOrNetworkAuthHeaders(input, { ...init, headers });
}

export function accountAuthHeaders() {
    const accountId = requireAccessScope("account");
    return buildAuthHeaders(accountId);
}
